import React, { useState, useEffect, useRef } from 'react';
import { TagChip } from './TagChip';

export interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  onSearch?: (query: string) => Promise<string[]>;
  placeholder?: string;
  maxTags?: number;
  disabled?: boolean;
}

export const TagInput: React.FC<TagInputProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Type tags...',
  maxTags,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputValue.trim() && onSearch) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const results = await onSearch(inputValue.trim());
          setSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching tag suggestions:', error);
          setSuggestions([]);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, onSearch]);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    
    if (!trimmedTag) return;
    if (value.includes(trimmedTag)) return;
    if (maxTags && value.length >= maxTags) return;

    const newTags = [...value, trimmedTag];
    onChange(newTags);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const removeTag = (index: number) => {
    const newTags = value.filter((_, i) => i !== index);
    onChange(newTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      e.preventDefault();
      removeTag(value.length - 1);
    }
  };

  const handleSuggestionClick = (suggestion: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addTag(suggestion);
    // Use setTimeout to ensure focus happens after click event completes
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <div className="tag-input-container">
      <div className="tag-input-chips">
        {value.map((tag, index) => (
          <TagChip
            key={index}
            value={tag}
            onRemove={() => removeTag(index)}
          />
        ))}
      </div>
      
      <div className="tag-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="tag-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        
        {showSuggestions && suggestions.length > 0 && (
          <div className="tag-input-suggestions">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="tag-input-suggestion"
                onClick={(e) => handleSuggestionClick(suggestion, e)}
                onMouseDown={(e) => e.preventDefault()} // Prevent input blur
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

